// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
// import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown"; // Store Custom Code
// import React, { FC, useEffect, useState } from "react"; // Store Custom Code
import { connect, ResolveThunks } from "react-redux";

/* Store Custom Code - Start */
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import Tooltip from "@insite/mobius/Tooltip";
import { getCookie } from "@insite/mobius/utilities/cookies";
import DynamicDropdown, {
    DynamicDropdownPresentationProps,
    OptionObject,
} from "@store/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import constants from "@store/constants";
import useHasPageLoadedWithMultipleCustomer from "@store/Framework/Common/Hooks/useHasPageLoadedWithMultipleCustomer";
import useIsModalOverlay from "@store/Framework/Common/Hooks/useIsModalOverlay";
import debounce from "lodash/debounce";
import React, { FC, useCallback, useEffect, useState } from "react";
/* Store Custom Code - End */

interface OwnProps {
    noShipToAndCantCreate: boolean;
    setParameter: (parameter: GetBillTosApiParameter) => void;
    parameter: GetBillTosApiParameter;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
    extendedStyles?: ChangeCustomerBillToSelectorStyles;
    isLoading?: boolean;
    // Store Custom Code
    soldTosInstructions?: string;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    billTosDataView: getBillTosDataView(state, props.parameter),
    customerSettings: getSettingsCollection(state).customerSettings,
    // Store Custom Code
    currentBillToState: getCurrentBillToState(state),
});

const mapDispatchToProps = {
    loadBillTos,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangeCustomerBillToSelectorStyles {
    billToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const changeCustomerBillToSelectorStyles: ChangeCustomerBillToSelectorStyles = {};

const ChangeCustomerBillToSelector: FC<Props> = ({
    noShipToAndCantCreate,
    parameter,
    setParameter,
    billTo,
    onSelect,
    extendedStyles,
    isLoading,
    billTosDataView,
    // Store Custom Code
    soldTosInstructions,
    currentBillToState,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerBillToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);

    /* Store Custom Code - Start */
    const hasMultipleCustomers = useHasPageLoadedWithMultipleCustomer(billTosDataView.value);
    const [inputText, setInputText] = useState<string>();
    const { useOverlay, scrollElement } = useIsModalOverlay();
    /* Store Custom Code - End */

    useEffect(() => {
        if (billTosDataView.value) {
            const options: OptionObject[] = billTosDataView.value.map(billTo => ({
                optionText: billTo.label,
                optionValue: billTo.id,
            }));
            setOptions(options);
            /* Store Custom Code - Start */
            const showChangeCustomerPageVariantCookie = getCookie(constants.cookies.showChangeCustomerPageVariant);

            if (billTosDataView.value.length === 1 && !hasMultipleCustomers && showChangeCustomerPageVariantCookie) {
                onSelect(billTosDataView.value[0]);
            }
            /* Store Custom Code - End */
        }
    }, [billTosDataView]);

    /* Store Custom Code - Start */
    const debouncedSearchBillto = useCallback(
        debounce((query: string) => {
            setParameter({
                ...parameter,
                filter: query,
            });
        }, constants.searchDebounceDelay),
        [setParameter],
    );
    /* Store Custom Code - End */

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        /* Store Custom Code - Start */
        const newValue = event.currentTarget.value;
        setInputText(newValue);
        debouncedSearchBillto(newValue);
        /* Store Custom Code - End */
    };

    const selectCustomerHandler = (billToId?: string) => {
        if (billTosDataView.value && billToId) {
            const billTo = billTosDataView.value.filter(billTo => billTo.id === billToId)[0];
            if (billTo) {
                onSelect(billTo as BillToModel);
            }
        }
    };

    /* Store Custom Code - Start */
    const oneBillToId =
        billTosDataView.value && billTosDataView.value.length === 1 && !hasMultipleCustomers
            ? billTosDataView.value[0].id
            : undefined;
    const selectedItemId = oneBillToId || billTo?.id;

    const onCloseHandler = () => {
        if (billTo) {
            setInputText(undefined);

            const selectedOption = options.find(x => x.optionValue === billTo.id);

            if (selectedOption) {
                setOptions(options);
            } else {
                setOptions([
                    {
                        optionText: billTo.label,
                        optionValue: billTo.id,
                    },
                ]);
            }
            setParameter({
                ...parameter,
                filter: undefined,
            });
        }
    };

    useEffect(() => {
        const currentBillTo = currentBillToState && currentBillToState.value;
        if (currentBillTo) {
            onSelect(currentBillTo as BillToModel);
        }
    }, []);

    useEffect(() => {
        const selectedOption = options.find(x => x.optionValue === billTo?.id);
        if (!!billTo && !selectedOption && !inputText) {
            setOptions(opt => [
                ...opt,
                {
                    optionText: billTo?.label || "",
                    optionValue: billTo?.id,
                },
            ]);
        }
    }, [options, billTo, inputText]);
    /* Store Custom Code - End */

    return (
        <DynamicDropdown
            /* Store Custom Code - Start */
            label={
                <>
                    {translate("Select Bill To")}
                    {soldTosInstructions && <Tooltip text={soldTosInstructions} />}
                </>
            }
            /* Store Custom Code - End */
            {...styles.billToSelectorDynamicDropdown}
            onSelectionChange={selectCustomerHandler}
            onInputChange={searchTextChanged}
            selected={selectedItemId} // Store Custom Code
            options={options}
            placeholder={translate("Search or Select Bill To")}
            isLoading={isLoading}
            error={noShipToAndCantCreate && siteMessage("SignIn_NoShipToAndCantCreate")}
            data-test-selector="changeCustomerBillToSelector"
            filterOption={() => true}
            uid="ChangeCustomerBillToSelector" // Starter Custom Code
            // Store Custom Code
            disabled={Boolean(oneBillToId)} // Store Custom Code
            onClose={onCloseHandler}
            /* Store Custom Code - Start */
            useOverlay={useOverlay}
            scrollElement={scrollElement}
            /* Store Custom Code - End */
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerBillToSelector);
